/**
 * Centralized Logging Utility
 * 
 * Provides environment-aware logging that:
 * - Only logs in development mode
 * - Supports different log levels
 * - Can be extended to send logs to error tracking services
 * - Prevents sensitive data exposure in production
 */

import { env, isProduction } from "../config/env";

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

// Get log level from environment or default to WARN in production, DEBUG in development
const getLogLevel = () => {
  if (isProduction) {
    const configured = env.logLevel ? LOG_LEVELS[env.logLevel.toUpperCase()] : undefined;
    return configured ?? LOG_LEVELS.WARN;
  }
  return LOG_LEVELS.DEBUG; // All logs in development
};

const currentLogLevel = getLogLevel();

/**
 * Checks if a log level should be logged
 * @param {number} level - Log level to check
 * @returns {boolean} Whether to log
 */
const shouldLog = (level) => {
  return level >= currentLogLevel;
};

/**
 * Sanitizes data before logging to prevent sensitive information exposure
 * @param {*} data - Data to sanitize
 * @returns {*} Sanitized data
 */
const sanitizeLogData = (data) => {
  if (!data) return data;
  
  // Don't log sensitive fields
  const sensitiveFields = ['password', 'token', 'auth', 'authorization', 'apiKey', 'secret'];
  
  if (typeof data === 'object') {
    const sanitized = Array.isArray(data) ? [...data] : { ...data };
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveFields.some(field => lowerKey.includes(field))) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = sanitizeLogData(sanitized[key]);
      }
    });
    
    return sanitized;
  }
  
  return data;
};

/**
 * Debug level logging (development only)
 * @param {...*} args - Arguments to log
 */
export const debug = (...args) => {
  if (shouldLog(LOG_LEVELS.DEBUG)) {
    const sanitized = args.map(sanitizeLogData);
    console.debug('[DEBUG]', ...sanitized);
  }
};

/**
 * Info level logging
 * @param {...*} args - Arguments to log
 */
export const info = (...args) => {
  if (shouldLog(LOG_LEVELS.INFO)) {
    const sanitized = args.map(sanitizeLogData);
    console.info('[INFO]', ...sanitized);
  }
};

/**
 * Warning level logging
 * @param {...*} args - Arguments to log
 */
export const warn = (...args) => {
  if (shouldLog(LOG_LEVELS.WARN)) {
    const sanitized = args.map(sanitizeLogData);
    console.warn('[WARN]', ...sanitized);
  }
};

/**
 * Error level logging
 * @param {Error|string} error - Error to log
 * @param {string} context - Context where error occurred
 * @param {Object} additionalData - Additional data to log
 */
export const error = (error, context = 'Unknown', additionalData = {}) => {
  if (shouldLog(LOG_LEVELS.ERROR)) {
    const sanitizedData = sanitizeLogData(additionalData);
    
    console.error(`[ERROR] [${context}]`, error);
    
    if (Object.keys(sanitizedData).length > 0) {
      console.error('Additional data:', sanitizedData);
    }
    
    // Log stack trace if available
    if (error?.stack) {
      console.error('Stack trace:', error.stack);
    }
    
    // In production, you can send to error tracking service
    if (isProduction) {
      // Example: Send to error tracking service
      // errorTrackingService.captureException(error, {
      //   context,
      //   ...sanitizedData
      // });
    }
  }
};

/**
 * Logs API request/response for debugging
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} data - Request/response data
 * @param {string} type - 'request' or 'response'
 */
export const logAPI = (method, url, data = {}, type = 'request') => {
  if (shouldLog(LOG_LEVELS.DEBUG)) {
    const sanitized = sanitizeLogData(data);
    console.debug(`[API ${type.toUpperCase()}] ${method} ${url}`, sanitized);
  }
};

/**
 * Logs performance metrics
 * @param {string} label - Performance label
 * @param {number} duration - Duration in milliseconds
 */
export const logPerformance = (label, duration) => {
  if (shouldLog(LOG_LEVELS.DEBUG)) {
    console.debug(`[PERF] ${label}: ${duration}ms`);
  }
};

/**
 * Grouped logging for related logs
 * @param {string} label - Group label
 * @param {Function} fn - Function to execute within group
 */
export const group = (label, fn) => {
  if (shouldLog(LOG_LEVELS.DEBUG)) {
    console.group(label);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  } else {
    fn();
  }
};

/**
 * Table logging for structured data
 * @param {*} data - Data to log as table
 */
export const table = (data) => {
  if (shouldLog(LOG_LEVELS.DEBUG)) {
    const sanitized = sanitizeLogData(data);
    console.table(sanitized);
  }
};

// Default export with all logging functions
const logger = {
  debug,
  info,
  warn,
  error,
  logAPI,
  logPerformance,
  group,
  table,
  LOG_LEVELS
};

export default logger;

